import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function AdminAuctionDetailPage() {
  const { aktenzeichen } = useParams<{ aktenzeichen: string }>();
  const [auction, setAuction] = useState<any>(null);
  const [bids, setBids] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (aktenzeichen) loadAuction(); }, [aktenzeichen]);

  async function loadAuction() {
    const { data: a } = await supabase.from('auctions').select('*').eq('aktenzeichen', aktenzeichen).single();
    if (!a) { setLoading(false); return; }
    setAuction(a);
    const { data: b } = await supabase.from('bids').select('*, profiles!inner(companies(name, ansprechpartner_vorname, ansprechpartner_nachname))').eq('auction_id', a.id).order('betrag', { ascending: false });
    setBids(b || []);
    setLoading(false);
  }

  async function updateStatus(newStatus: string) {
    if (!auction) return;
    await supabase.from('auctions').update({ status: newStatus }).eq('id', auction.id);
    loadAuction();
  }

  async function extendHours(hours: number) {
    if (!auction) return;
    const d = new Date(auction.end_at);
    d.setHours(d.getHours() + hours);
    await supabase.from('auctions').update({ end_at: d.toISOString() }).eq('id', auction.id);
    loadAuction();
  }

  if (loading) return <div className="container-content py-12"><p className="text-text-secondary">Laden...</p></div>;
  if (!auction) return <div className="container-content py-12"><p className="text-text-secondary">Nicht gefunden.</p></div>;

  return (
    <div className="container-content py-12">
      <nav className="text-sm text-text-muted mb-4">
        <Link to="/admin" className="">Admin</Link> <span className="mx-2">/</span>
        <Link to="/admin/auktionen" className="">Auktionen</Link> <span className="mx-2">/</span>
        <span>{auction.aktenzeichen}</span>
      </nav>
      <h1 className="text-2xl font-serif mb-6">{auction.aktenzeichen} — {auction.titel}</h1>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <Stat label="Status" value={auction.status} />
        <Stat label="Gebote" value={String(bids.length)} />
        <Stat label="Höchstgebot" value={bids.length > 0 ? formatCurrency(bids[0].betrag) : '—'} />
        <Stat label="Ende" value={formatDateTime(auction.end_at)} />
      </div>
      <div className="mb-8 p-4 border border-surface-border rounded-sm">
        <h2 className="text-lg font-serif mb-3">Aktionen</h2>
        <div className="flex flex-wrap gap-2">
          {auction.status === 'LAUFEND' && (<>
            <button onClick={() => extendHours(1)} className="btn-secondary">+1h</button>
            <button onClick={() => extendHours(24)} className="btn-secondary">+24h</button>
            <button onClick={() => updateStatus('BEENDET_OHNE_ZUSCHLAG')} className="btn-danger">Sofort beenden</button>
          </>)}
          <Link to={`/admin/auktionen/${auction.aktenzeichen}/analytics`} className="btn-secondary">Analytics</Link>
        </div>
      </div>
      <h2 className="text-lg font-serif mb-3">Alle Gebote mit Klarnamen</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead><tr><th>Rang</th><th>Betrag</th><th>Zeit</th><th>Bieter</th><th>Unternehmen</th><th>Bietnr.</th><th>IP</th></tr></thead>
          <tbody>
            {bids.map((bid, idx) => (
              <tr key={bid.id}>
                <td>{idx + 1}</td>
                <td className="font-mono">{formatCurrency(bid.betrag)}</td>
                <td>{formatDateTime(bid.abgegeben_at)}</td>
                <td>{bid.profiles?.companies?.ansprechpartner_vorname} {bid.profiles?.companies?.ansprechpartner_nachname}</td>
                <td>{bid.profiles?.companies?.name}</td>
                <td className="font-mono">{bid.bietnummer_anonym}</td>
                <td className="font-mono text-xs">{bid.ip_adresse}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div className="p-4 border border-surface-border rounded-sm"><div className="text-sm text-text-muted">{label}</div><div className="font-serif">{value}</div></div>;
}
